import { useRef, useEffect, useState } from 'react';

const WHATSAPP_NUMBER = '5579999778899';

// Títulos dos cards da seção "Consultoria Jurídica" (Atuação)
const AREA_OPTIONS = [
  'Orientação Jurídica Preventiva',
  'Análise de Riscos',
  'Suporte na Tomada de Decisões',
  'Atendimento Personalizado',
];

const CONTACT_CARDS = [
  {
    label: 'WhatsApp',
    value: '(79) 99977-8899',
    link: 'https://wa.me/5579999778899',
    linkText: 'Enviar mensagem',
  },
  {
    label: 'E-mail',
    value: 'contato@profdiogocalasans.org',
    link: 'mailto:contato@profdiogocalasans.org',
    linkText: 'Enviar e-mail',
  },
  {
    label: 'Telefone',
    value: '(79) 99977-8899',
    link: 'tel:+5579999778899',
    linkText: 'Ligar agora',
  },
  {
    label: 'Endereço',
    value: 'Av. Ministro Geraldo Barreto Sobral, 162, sala 5\nGrageru – Aracaju/SE – CEP 49027-255',
    link: null,
    linkText: null,
  },
  {
    label: 'Instagram',
    value: '@diogocalasans',
    link: 'https://instagram.com/diogocalasans',
    linkText: 'Seguir no Instagram',
  },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [area, setArea] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validação simples: todos os campos são obrigatórios
    if (!name.trim() || !email.trim() || !area.trim() || !message.trim()) return;

    // Monta a mensagem no formato exato solicitado
    const text = `Nome Completo: ${name} | E-mail: ${email} | Área de Interesse: ${area} | Motivo do Contato: ${message}`;

    // Codifica para URL e abre WhatsApp em nova aba
    const encoded = encodeURIComponent(text);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const inputClass =
    'w-full px-4 text-sm border border-[#C9D3DA] rounded-lg bg-white text-[#1F4B63] font-400 outline-none focus:border-[#C6935A] focus:ring-1 focus:ring-[#C6935A]/30 transition-all duration-200 placeholder:text-[#9AB0BD]';

  return (
    <section id="contato" className="py-32 bg-[#EAF0F4]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 anim opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#C6935A] text-xs font-600 tracking-[3px] uppercase mb-4">
            Fale Conosco
          </p>
          <h2 className="text-4xl lg:text-5xl font-700 text-[#1F4B63] leading-tight mb-4">
            Contato
          </h2>
          <div className="w-16 h-0.5 bg-[#C6935A] mx-auto mt-4 mb-6" />
          <p className="text-[#4A5568] text-lg max-w-xl mx-auto">
            Estamos à disposição para orientar você. Entre em contato e responderemos com brevidade.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <div className="anim opacity-0 translate-y-8 transition-all duration-700">
            <div className="bg-white rounded-2xl p-8 lg:p-10">
              <h3 className="text-[#1F4B63] font-700 text-xl mb-7">Envie sua mensagem</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Nome Completo */}
                <div>
                  <label className="block text-[#173A4D] text-sm font-600 mb-2">Nome completo</label>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Seu nome"
                    className={inputClass}
                    style={{ height: '52px' }}
                  />
                </div>

                {/* E-mail */}
                <div>
                  <label className="block text-[#173A4D] text-sm font-600 mb-2">E-mail</label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="seu@email.com"
                    className={inputClass}
                    style={{ height: '52px' }}
                  />
                </div>

                {/* Área de Interesse — select obrigatório com títulos da seção "Atuação" */}
                <div>
                  <label className="block text-[#173A4D] text-sm font-600 mb-2">Área de Interesse</label>
                  <select
                    name="area"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    required
                    className={`${inputClass} appearance-none`}
                    style={{ height: '52px' }}
                  >
                    <option value="" disabled>
                      Selecione uma área
                    </option>
                    {AREA_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mensagem */}
                <div>
                  <label className="block text-[#173A4D] text-sm font-600 mb-2">
                    Mensagem
                    <span
                      className={`ml-2 text-xs font-400 ${
                        message.length > 500 ? 'text-red-500' : 'text-[#9AB0BD]'
                      }`}
                    >
                      ({message.length}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Descreva brevemente sua questão jurídica..."
                    className={`${inputClass} resize-none`}
                    style={{ height: '140px' }}
                  />
                  {message.length > 500 && (
                    <p className="text-red-500 text-xs mt-1">
                      A mensagem não pode ultrapassar 500 caracteres.
                    </p>
                  )}
                </div>

                {/* Botão Enviar */}
                <button
                  type="submit"
                  disabled={!name.trim() || !email.trim() || !area.trim() || !message.trim() || message.length > 500}
                  className="w-full h-14 flex items-center justify-center gap-3 bg-[#C6935A] hover:bg-[#A8763F] disabled:opacity-60 text-white font-600 rounded-lg transition-all duration-300 text-base cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-2-fill text-white text-lg" />
                  Enviar Mensagem
                </button>
              </form>
            </div>
          </div>

          {/* Info Column */}
          <div className="anim opacity-0 translate-y-8 transition-all duration-700 delay-200 space-y-4">
            {CONTACT_CARDS.map((info) => (
              <div key={info.label} className="bg-white rounded-xl p-5">
                <p className="text-[#9AB0BD] text-xs font-600 tracking-wide uppercase mb-1">
                  {info.label}
                </p>
                <p className="text-[#1F4B63] font-500 text-sm whitespace-pre-line">
                  {info.value}
                </p>
                {info.link && (
                  <a
                    href={info.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#C6935A] text-xs font-600 mt-1 inline-block hover:text-[#A8763F] transition-colors cursor-pointer"
                  >
                    {info.linkText} &rarr;
                  </a>
                )}
              </div>
            ))}

            {/* Google Map */}
            <div className="rounded-xl overflow-hidden" style={{ height: '220px' }}>
              <iframe
                title="Localização do escritório"
                src="https://maps.google.com/maps?q=Av.+Ministro+Geraldo+Barreto+Sobral+162+Grageru+Aracaju+SE&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}